import { Link } from "react-router-dom";

import { labeebBusinessName, labeebBusinessRef, type LabeebTenantSummary } from "../../../api/labeeb";
import { blockedUserCount, tenantsOf, type McpTenantList } from "../../../api/mcp";
import { ResourceView } from "../../../components/states";
import { Badge, Card, Notice, Table, Td, Th } from "../../../components/ui";
import type { PageOf } from "../../../hooks/usePagedList";
import type { Resource } from "../../../hooks/useResource";
import { ProductTag } from "../../../products/PerProduct";

interface DecisionRow {
  label: string;
  sub: string;
  why: string;
  tone: "amber" | "red";
  to: string;
}

/**
 * MCP blocks plus suspended Labeeb businesses. Nothing else belongs here —
 * an uncapped tier or a disabled domain is a setting, not a decision.
 *
 * A failed read is a notice beside the rows the other product did return.
 * "Nothing needs a decision" is only said when both lists were read and
 * neither had anything to act on. A cursor still open means later pages may
 * hold more suspensions, so that sentence stays unsaid.
 */
export function DecisionPanel({
  tenants,
  businesses,
}: {
  tenants: { state: Resource<McpTenantList>; reload: () => void };
  businesses: { state: Resource<PageOf<LabeebTenantSummary>>; reload: () => void };
}) {
  const mcpState = tenants.state;
  const labeebState = businesses.state;
  const mcpReady = mcpState.kind === "ready";
  const labeebReady = labeebState.kind === "ready";
  const labeebRows = labeebState.kind === "ready" ? labeebState.data.rows : [];
  const partial = labeebState.kind === "ready" && labeebState.data.next !== null;
  const rows = decisionRows(mcpState.kind === "ready" ? mcpState.data : null, labeebRows);

  return (
    <Card
      title="Needs a decision"
      note="MCP blocks and suspended Labeeb businesses"
      aside={
        <>
          {mcpReady && labeebReady && <DecisionCount n={rows.length} partial={partial} />}
          <ProductTag id="mcp" />
          <ProductTag id="labeeb" />
        </>
      }
    >
      {tenants.state.kind !== "ready" && (
        <ResourceView state={tenants.state} what="MCP's merchants" onRetry={tenants.reload} />
      )}
      {businesses.state.kind !== "ready" && (
        <ResourceView
          state={businesses.state}
          what="Labeeb's businesses"
          onRetry={businesses.reload}
        />
      )}
      {rows.length > 0 && <DecisionTable rows={rows} />}
      {partial && (
        <Notice title="Labeeb's list continues">
          This queue has the first page only. Later pages may include more suspended businesses.
        </Notice>
      )}
      {mcpReady && labeebReady && !partial && rows.length === 0 && (
        <Notice title="Nothing needs a decision">No blocks or suspensions in force.</Notice>
      )}
    </Card>
  );
}

/**
 * What needs acting on, from the two lists the page already reads.
 *
 * Shared by the panel and the count in its header, so the badge can never
 * disagree with the rows beneath it. `tenants` null means MCP's list was not
 * read — the rows then are only the suspensions we did see.
 */
function decisionRows(tenants: McpTenantList | null, labeeb: LabeebTenantSummary[]): DecisionRow[] {
  const rows: DecisionRow[] = [];

  if (tenants) {
    for (const t of tenantsOf(tenants)) {
      const whys: string[] = [];
      let tone: "amber" | "red" = "amber";
      if (t.blocked) {
        whys.push("blocked merchant-wide");
        tone = "red";
      }
      // Through the helper: dev has already dropped the names array for a count,
      // and reading `.length` off the absent field is what took this page down.
      const blockedHere = blockedUserCount(t);
      if (blockedHere) {
        whys.push(`${blockedHere} user${blockedHere === 1 ? "" : "s"} blocked here`);
      }
      if (whys.length) {
        rows.push({
          label: t.business_name,
          sub: t.business_reference,
          why: whys.join(" · "),
          tone,
          to: `/tenants/${encodeURIComponent(t.business_reference)}`,
        });
      }
    }
    for (const e of tenants.users_blocked_across_all_merchants ?? []) {
      rows.push({
        label: e,
        sub: "every merchant",
        why: "blocked fleet-wide — not attached to any one merchant",
        tone: "amber",
        to: "/policy/access",
      });
    }
  }

  for (const business of labeeb) {
    if (business.status !== "suspended") continue;
    const reference = labeebBusinessRef(business);
    if (!reference) continue;
    rows.push({
      label: labeebBusinessName(business) || reference,
      sub: reference,
      why: "suspended",
      tone: "red",
      to: `/tenants/${encodeURIComponent(reference)}`,
    });
  }
  return rows;
}

/** The design's "N open" badge. A "+" means Labeeb's list still has pages. */
function DecisionCount({ n, partial }: { n: number; partial: boolean }) {
  // Green only when both lists were read to the end and nothing was found.
  // A cursor still open is not "all clear" — a later page may hold a suspension.
  const open = n > 0 || partial;
  return <Badge tone={open ? "amber" : "green"}>{partial ? `${n}+ open` : `${n} open`}</Badge>;
}

function DecisionTable({ rows }: { rows: DecisionRow[] }) {
  return (
    // `[--gutter:0px]` is the design's own setting for a table inside a card:
    // F7's Table indents its cells by a page gutter, which double-padded every
    // one of these and pushed the first column off the panel's own text edge.
    <Table
      className="[--gutter:0px]"
      head={
        <tr>
          <Th>Subject</Th>
          <Th>Why</Th>
        </tr>
      }
    >
      {rows.map((r, i) => (
        <tr key={`${r.to}-${r.why}-${i}`}>
          <Td>
            <Link to={r.to} className="font-medium text-brand-600 hover:underline dark:text-brand-300">
              <span dir="auto">{r.label}</span>
            </Link>
            <div className="ref text-xs text-slate-400 dark:text-slate-500">{r.sub}</div>
          </Td>
          {/* `max-w-xs`, the design's own width on this cell. Without it the
              longest reason ran past the panel's edge and was clipped by the
              card's overflow — the row that most needed reading was the one
              that lost its last words. */}
          <Td>
            <span
              className={`block max-w-xs whitespace-normal text-xs ${
                r.tone === "red"
                  ? "text-red-600 dark:text-red-400"
                  : "text-amber-700 dark:text-amber-400"
              }`}
            >
              {r.why}
            </span>
          </Td>
        </tr>
      ))}
    </Table>
  );
}
